/**
 * Miracle Air OS | FRMS Integration Service
 * 
 * Integrates with external FRMS system via backend proxy to fetch pilot data:
 * - Pilot profiles and compliance
 * - Total flight time (TT)
 * - License compliance and expiry
 * - Current base/location
 * - Duty status and metrics
 * 
 * Note: API key is stored securely on backend. Frontend calls backend proxy endpoints.
 */

import api from './api';

// Use backend proxy instead of calling FRMS directly
// Note: api client already has baseURL='/maos/api', so we only need the path after /api
const FRMS_PROXY_BASE = '/frms-proxy';

interface FRMSUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  totalFlightHours?: number;
  licenseExpiry?: string;
  currentBase?: string;
  licenseNumber?: string;
  licenseType?: string;
  complianceStatus?: 'VALID' | 'EXPIRING_SOON' | 'EXPIRED';
}

interface FRMSMetrics {
  userId: string;
  totalFlightHours: number;
  totalDutyHours: number;
  licenseExpiry?: string;
  currentBase?: string;
  complianceStatus?: 'VALID' | 'EXPIRING_SOON' | 'EXPIRED';
  lastDutyDate?: string;
  dutyHoursToday?: number;
  dutyHoursThisWeek?: number;
  dutyHoursThisMonth?: number;
}

interface FRMSDuty {
  id: string;
  pilotId: string;
  pilotName?: string;
  type: 'FLIGHT' | 'SIMULATOR' | 'GROUND' | 'STANDBY' | 'OFF' | 'TRAINING';
  date: string;
  scheduledDeparture?: string;
  scheduledArrival?: string;
  route?: string;
  aircraftRegistration?: string | null;
  dutyDurationHours: number;
  displayHours: number;
  breakdown?: string;
  status: 'NORMAL' | 'WARNING' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  createdAt?: string;
  updatedAt?: string;
}

// Log configuration on module load (for debugging)
if (typeof window !== 'undefined') {
  console.log('[FRMS Service] Using backend proxy:', FRMS_PROXY_BASE);
}

export const frmsService = {
  /**
   * Get all pilots from FRMS system via backend proxy
   * Requires scope: users:read
   */
  getAllPilots: async (): Promise<FRMSUser[]> => {
    try {
      console.log(`[FRMS Service] Fetching all pilots from ${FRMS_PROXY_BASE}/users`);
      const response = await api.get(`${FRMS_PROXY_BASE}/users`);
      console.log(`[FRMS Service] Response:`, { 
        success: response.data?.success, 
        dataLength: Array.isArray(response.data?.data) ? response.data.data.length : 'not array',
        firstPilot: Array.isArray(response.data?.data) && response.data.data.length > 0 ? {
          id: response.data.data[0].id,
          email: response.data.data[0].email,
          name: response.data.data[0].name,
        } : null
      });
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      console.error('[FRMS Service] Failed to fetch pilots:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
      });
      return [];
    }
  },

  /**
   * Get FRMS metrics for a specific pilot via backend proxy
   * Requires scope: reports:read
   */
  getPilotMetrics: async (userId: string): Promise<FRMSMetrics | null> => {
    try {
      console.log(`[FRMS Service] Fetching metrics for pilot ${userId} from ${FRMS_PROXY_BASE}/metrics/${userId}`);
      const response = await api.get(`${FRMS_PROXY_BASE}/metrics/${userId}`);
      console.log(`[FRMS Service] Response received:`, { 
        success: response.data?.success, 
        hasData: !!response.data?.data,
        dataKeys: response.data?.data ? Object.keys(response.data.data).slice(0, 5) : []
      });
      const data = response.data?.data || response.data;
      return data;
    } catch (error: any) {
      console.error(`[FRMS Service] Failed to fetch metrics for pilot ${userId}:`, {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
      });
      return null;
    }
  },

  /**
   * Get pilot profile by email (for matching with MA-OS users)
   * Requires scope: users:read
   */
  getPilotByEmail: async (email: string): Promise<FRMSUser | null> => {
    try {
      const pilots = await frmsService.getAllPilots();
      return pilots.find(p => p.email?.toLowerCase() === email.toLowerCase()) || null;
    } catch (error) {
      console.error(`[FRMS Service] Failed to find pilot by email ${email}:`, error);
      return null;
    }
  },

  /**
   * Get duties for a pilot within a date range via backend proxy
   * Requires scope: duties:read
   */
  getPilotDuties: async (
    pilotId: string,
    startDate: string,
    endDate: string
  ): Promise<FRMSDuty[]> => {
    try {
      const response = await api.get(`${FRMS_PROXY_BASE}/duties`, {
        params: { all: true, startDate, endDate, pilotId },
      });
      const data = response.data?.data || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      console.error(`[FRMS Service] Failed to fetch duties for pilot ${pilotId}:`, error?.message || error);
      return [];
    }
  },

  /**
   * Get today's duties for a pilot
   */
  getTodayDuties: async (pilotId: string): Promise<FRMSDuty[]> => {
    const today = new Date().toISOString().split('T')[0];
    return frmsService.getPilotDuties(pilotId, today, today);
  },

  /**
   * Create a duty record in FRMS via backend proxy
   * Requires scope: duties:write
   */
  createDuty: async (duty: {
    pilotId: string;
    type: 'FLIGHT' | 'SIMULATOR' | 'GROUND' | 'STANDBY';
    date: string;
    scheduledDeparture?: string;
    scheduledArrival?: string;
    route?: string;
  }): Promise<FRMSDuty> => {
    try {
      const response = await api.post(`${FRMS_PROXY_BASE}/duties`, duty);
      const data = response.data?.data || response.data;
      return data;
    } catch (error: any) {
      console.error('[FRMS Service] Failed to create duty:', error?.message || error);
      throw error;
    }
  },
};

export type { FRMSUser, FRMSMetrics, FRMSDuty };

